<script setup lang="ts">
// McpSidebar (ORP-21466) — the MCP-only rail. Written from scratch rather than reusing
// `AppSidebar`, which is driven by `useNavigation()`: that tree is the web product's, built from
// reports/dashboards/permissions, and filtering it down to four items would leave the MCP surface
// one nav-registry change away from sprouting a Reports entry again.
//
// EXACTLY FOUR items, hardcoded in one array so "no other item" is a property of the file, not of
// a runtime filter. The design also draws a "Home" row above them; it is NOT ported — the ticket
// says four and there is no fifth screen for it to open.
//
// Read Only users see Users (AC 6). Hiding the item would leave them unable to see who is on the
// account; the INVITE control inside that screen is what their role withholds (wave 2's job).
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import McpPlanChip from './McpPlanChip.vue'
import McpWorkspacePicker from './McpWorkspacePicker.vue'

import { useNavLabels } from '@/app/navigation/useNavLabels'
import { useAccount } from '@/features/account'
import { AuthBrandLogo } from '@/features/auth'
import { useI18n } from '@/shared/i18n'
import { Icon, type IconName } from '@/shared/ui/icon'

interface McpNavItem {
  name: string
  labelKey: string
  icon: IconName
}

/** The whole MCP navigation. Adding a fifth entry here is the only way to grow the menu. */
const NAV_ITEMS: readonly McpNavItem[] = [
  { name: 'McpAdAccounts', labelKey: 'shell.mcp.nav.adAccounts', icon: 'ad' },
  { name: 'McpAssistants', labelKey: 'shell.mcp.nav.assistants', icon: 'message-dot' },
  { name: 'McpUsers', labelKey: 'shell.mcp.nav.users', icon: 'users' },
  { name: 'McpPlanBilling', labelKey: 'shell.mcp.nav.planBilling', icon: 'coins' },
] as const

const { t } = useI18n()
// Item label keys are data, not template-authored copy — the shell's one translation seam for
// them (the same one `NavTreeItem` uses), which is why `no-dynamic-keys` stays on here.
const { labelOf } = useNavLabels()
const route = useRoute()
const { userEmail } = useAccount()

const isCurrent = (name: string): boolean => route.name === name
const email = computed(() => userEmail.value ?? '')
const initial = computed(() => email.value.slice(0, 1).toUpperCase())
</script>

<template>
  <aside class="mcp-sidebar" data-testid="mcp-sidebar">
    <div class="mcp-sidebar__brand">
      <AuthBrandLogo />
      <span class="mcp-sidebar__badge">{{ t('shell.mcp.badge') }}</span>
    </div>

    <McpWorkspacePicker />

    <nav class="mcp-sidebar__nav" :aria-label="t('shell.mcp.nav.label')">
      <ul class="mcp-sidebar__tree">
        <li v-for="item in NAV_ITEMS" :key="item.name">
          <RouterLink
            class="mcp-sidebar__link"
            :class="{ 'mcp-sidebar__link--current': isCurrent(item.name) }"
            :to="{ name: item.name }"
            :aria-current="isCurrent(item.name) ? 'page' : undefined"
          >
            <Icon :name="item.icon" :size="15" aria-hidden="true" />
            <span>{{ labelOf(item.labelKey) }}</span>
          </RouterLink>
        </li>
      </ul>
    </nav>

    <McpPlanChip />

    <p v-if="email" class="mcp-sidebar__user" data-testid="mcp-sidebar-user">
      <span class="mcp-sidebar__avatar" aria-hidden="true">{{ initial }}</span>
      <span class="mcp-sidebar__email">{{ email }}</span>
    </p>
  </aside>
</template>

<style scoped>
.mcp-sidebar {
  position: sticky;
  top: 0;
  display: flex;
  width: 13.75rem;
  height: 100dvh;
  flex-direction: column;
  gap: var(--ui-space-md);
  padding: var(--ui-space-md) var(--ui-space-sm);
  border-inline-end: 1px solid var(--ui-color-border);
  background: var(--ui-color-surface);
}
.mcp-sidebar__brand {
  display: flex;
  align-items: center;
  gap: var(--ui-space-xs);
}
.mcp-sidebar__brand :deep(.auth-brand-logo) {
  width: 5rem;
  max-height: 1.5rem;
  object-fit: contain;
}
.mcp-sidebar__badge {
  padding: 0.1875rem 0.5rem;
  border-radius: var(--ui-radius-full);
  background: var(--ui-color-surface-sunken);
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
  font-weight: 600;
}
.mcp-sidebar__tree {
  display: grid;
  gap: 0.125rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.mcp-sidebar__link {
  display: flex;
  align-items: center;
  gap: var(--ui-space-xs);
  padding: var(--ui-space-xs) 0.6875rem;
  border-radius: var(--ui-radius-md);
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: none;
}
.mcp-sidebar__link--current {
  background: var(--ui-color-surface-sunken);
  color: var(--ui-color-fg);
  font-weight: 600;
}
.mcp-sidebar__link:focus-visible {
  outline: 2px solid var(--ui-color-info);
  outline-offset: -2px;
}
.mcp-sidebar__user {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: var(--ui-space-xs);
  margin: 0;
}
.mcp-sidebar__avatar {
  display: grid;
  width: 1.625rem;
  height: 1.625rem;
  flex: none;
  place-items: center;
  border-radius: var(--ui-radius-full);
  background: var(--ui-color-primary);
  color: var(--ui-color-primary-fg);
  font-size: 0.6875rem;
  font-weight: 700;
}
.mcp-sidebar__email {
  overflow: hidden;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mobile (AC 7): the rail becomes a full-width band at the top of the page and the four items a
   horizontally scrollable row. No drawer, so all four stay one tap away with no state to open —
   the web shell's `navStore` drawer is deliberately not pulled in for four links. */
@media (max-width: 56rem) {
  .mcp-sidebar {
    position: static;
    width: 100%;
    height: auto;
    gap: var(--ui-space-sm);
    border-inline-end: 0;
    border-block-end: 1px solid var(--ui-color-border);
  }
  .mcp-sidebar__tree {
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    overflow-x: auto;
  }
  .mcp-sidebar__link {
    white-space: nowrap;
  }
}
</style>
